import React, { useState, useEffect, useMemo, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { http, getAuthorizationHeader, imageBaseUrl } from '../../config/http';
import Layouts from '../../layouts/Layouts';
import { PhotoProvider, PhotoView } from 'react-photo-view';
import 'react-photo-view/dist/react-photo-view.css';
import {
  IconCheck,
  IconX,
  IconEye,
  IconSearch,
  IconBuildingStore,
  IconReload,
  IconPhoto,
  IconInbox,
  IconChartPie,
  IconListDetails,
  IconUser,
  IconCircleCheck,
  IconClock,
  IconChevronLeft,
  IconChevronRight,
  IconAdjustmentsHorizontal,
  IconChevronUp,
  IconLayoutGrid,
  IconTable,
  IconCopy,
  IconRotateClockwise,
  IconCalendar,
  IconChevronDown,
} from '@tabler/icons-react';
import { DateRange } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import moment from 'moment';
import { toast } from 'react-toastify';
import PinBrankas from '../../components/Admin/PinBrankas';

const VerifikasiPermohonanBrankasV2 = () => {
  const token = localStorage.getItem('token');

  // Main list state
  const [permohonans, setPermohonans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState(''); // Default: Semua Status
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [totalPermohonan, setTotalPermohonan] = useState(0);
  const [pageInput, setPageInput] = useState('1');

  // View mode toggle: 'cards' or 'table' (default cards on mobile/tablet)
  const [viewMode, setViewMode] = useState('cards');

  // Collapsible Filter Panel Toggle
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  useEffect(() => {
    setPageInput(page.toString());
  }, [page]);

  const jumpToPage = () => {
    const p = parseInt(pageInput, 10);
    if (!isNaN(p) && p >= 1 && p <= lastPage) {
      setPage(p);
    } else {
      setPageInput(page.toString());
    }
  };

  // Date Filter State (Default: null / Semua Tanggal)
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const selectionRange = {
    startDate: startDate || new Date(),
    endDate: endDate || new Date(),
    key: 'selection',
  };

  const handleSelectDate = (date) => {
    setStartDate(date.selection.startDate);
    setEndDate(date.selection.endDate);
    setPage(1);
  };

  const awal = startDate ? moment(startDate).format('DD-MM-YYYY') : '';
  const akhir = endDate ? moment(endDate).format('DD-MM-YYYY') : '';

  const setQuickDate = (type) => {
    const today = new Date();
    if (type === 'today') {
      setStartDate(today);
      setEndDate(today);
    } else if (type === '7days') {
      const past = new Date();
      past.setDate(today.getDate() - 6);
      setStartDate(past);
      setEndDate(today);
    } else if (type === '30days') {
      const past = new Date();
      past.setDate(today.getDate() - 29);
      setStartDate(past);
      setEndDate(today);
    } else if (type === 'thisMonth') {
      const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
      setStartDate(firstDay);
      setEndDate(today);
    } else if (type === 'clear') {
      setStartDate(null);
      setEndDate(null);
    }
    setPage(1);
  };

  // Detail Modal State
  const [selectedPermohonan, setSelectedPermohonan] = useState(null);
  const [isOpenDetailModal, setIsOpenDetailModal] = useState(false);
  const [modalTab, setModalTab] = useState('ringkasan');
  const [searchModalItem, setSearchModalItem] = useState('');

  // Verification Action Modal State
  const [isOpenVerificationModal, setIsOpenVerificationModal] = useState(false);
  const [verifyingPermohonan, setVerifyingPermohonan] = useState(null);
  const [actionType, setActionType] = useState('disetujui');
  const [catatanVerifikasi, setCatatanVerifikasi] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPinModalVerification, setShowPinModalVerification] = useState(false);

  const [selectedUser, setSelectedUser] = useState('');
  const [selectedCabang, setSelectedCabang] = useState('');
  const [userList, setUserList] = useState([]);
  const [cabangList, setCabangList] = useState([]);

  // Detail Image Modal State (Paginated)
  const [isOpenImageModal, setIsOpenImageModal] = useState(false);
  const [selectedImagePermohonan, setSelectedImagePermohonan] = useState(null);
  const [imageItems, setImageItems] = useState([]);
  const [imagePage, setImagePage] = useState(1);
  const [imageLastPage, setImageLastPage] = useState(1);
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [totalModalPermohonan, setTotalModalPermohonan] = useState(0);

  // Copy to clipboard helper
  const handleCopyNoPermohonan = (e, noPermohonan) => {
    e.stopPropagation();
    if (navigator.clipboard) {
      navigator.clipboard.writeText(noPermohonan);
      toast.info(`No. Request ${noPermohonan} disalin`, { autoClose: 900 });
    }
  };

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const resUser = await http.get('users', { headers: { Authorization: getAuthorizationHeader() } });
        const users = resUser.data.data || resUser.data;
        setUserList(Array.isArray(users) ? users.filter(u => u.role && u.role.includes('operator_rumah')) : []);

        const resCabang = await http.get('cabang', { headers: { Authorization: getAuthorizationHeader() } });
        const cabangs = resCabang.data.data || resCabang.data;
        setCabangList(Array.isArray(cabangs) ? cabangs.map(c => ({ ...c, nama_cabang: c.nama_cabang ? c.nama_cabang.replace(/Etalase/gi, 'Brankas') : c.nama_cabang })) : []);
      } catch (err) {
        console.error(err);
      }
    };
    fetchOptions();
  }, [token]);

  const fetchPermohonanList = async () => {
    setLoading(true);
    try {
      let url = `permohonan-brankas?page=${page}&per_page=12`;
      if (statusFilter) url += `&status=${statusFilter}`;
      if (search) url += `&search=${search}`;
      if (selectedUser) url += `&user_pemohon_id=${selectedUser}`;
      if (selectedCabang) url += `&cabang_asal_id=${selectedCabang}`;
      if (startDate && endDate) {
        url += `&start_date=${moment(startDate).format('YYYY-MM-DD')}&end_date=${moment(endDate).format('YYYY-MM-DD')}`;
      }

      const res = await http.get(url, {
        headers: { Authorization: getAuthorizationHeader() }
      });
      if (res.data.status) {
        setPermohonans(res.data.data.data || []);
        setLastPage(res.data.data.last_page || 1);
        setTotalPermohonan(res.data.data.total || 0);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPermohonanList();
  }, [page, statusFilter, search, startDate, endDate, selectedUser, selectedCabang]);

  const fetchImageItems = async (permohonanId, pageNum) => {
    setIsImageLoading(true);
    try {
      const res = await http.get(`permohonan-brankas/${permohonanId}/items?page=${pageNum}&per_page=12`, {
        headers: { Authorization: getAuthorizationHeader() }
      });
      if (res.data.status) {
        setImageItems(res.data.data.data || []);
        setImageLastPage(res.data.data.last_page || 1);
        setTotalModalPermohonan(res.data.total_modal || 0);
      }
    } catch (error) {
      console.error(error);
      toast.error('Gagal mengambil data item gambar');
    } finally {
      setIsImageLoading(false);
    }
  };

  useEffect(() => {
    if (selectedImagePermohonan) {
      fetchImageItems(selectedImagePermohonan.id, imagePage);
    }
  }, [selectedImagePermohonan, imagePage]);

  const handleOpenImageModal = (permohonan) => {
    setSelectedImagePermohonan(permohonan);
    setImagePage(1);
    setIsOpenImageModal(true);
  };

  const handleOpenDetail = async (id) => {
    try {
      const res = await http.get(
        `permohonan-brankas/${id}`,
        { headers: { Authorization: getAuthorizationHeader() } }
      );
      if (res.data.status) {
        setSelectedPermohonan(res.data.data);
        setModalTab('ringkasan');
        setSearchModalItem('');
        setIsOpenDetailModal(true);
      }
    } catch (err) {
      console.error(err);
      toast.error('Gagal memuat detail permohonan');
    }
  };

  const detailSummary = useMemo(() => {
    if (!selectedPermohonan || !selectedPermohonan.items) return {};

    return selectedPermohonan.items.reduce((acc, item) => {
      const catName = item.jenis_kategori?.kategori?.nama_kategori || 'Tanpa Kategori';
      const karat = item.karat || '-';
      const berat = parseFloat(item.berat || 0);

      if (!acc[catName]) {
        acc[catName] = { name: catName, pcs: 0, berat: 0, byKarat: {} };
      }

      acc[catName].pcs += 1;
      acc[catName].berat += berat;

      if (!acc[catName].byKarat[karat]) {
        acc[catName].byKarat[karat] = { pcs: 0, berat: 0 };
      }

      acc[catName].byKarat[karat].pcs += 1;
      acc[catName].byKarat[karat].berat += berat;

      return acc;
    }, {});
  }, [selectedPermohonan]);

  const resolveItemImageUrl = (item) => {
    if (!item) return '';
    let gData = item.gambar1 || item.gambar;
    if (typeof gData === 'string' && (gData.startsWith('[') || gData.startsWith('{'))) {
      try {
        gData = JSON.parse(gData);
      } catch (e) {}
    }
    if (Array.isArray(gData) && gData.length > 0) {
      const path = gData[0]?.asli || gData[0]?.thumbnail || (typeof gData[0] === 'string' ? gData[0] : '');
      return path ? (path.startsWith('http') ? path : `${imageBaseUrl}${path}`) : '';
    }
    if (typeof gData === 'string' && gData.length > 0) {
      return gData.startsWith('http') ? gData : `${imageBaseUrl}${gData}`;
    }
    return '';
  };

  const filteredModalItems = useMemo(() => {
    if (!selectedPermohonan || !selectedPermohonan.items) return [];
    if (!searchModalItem.trim()) return selectedPermohonan.items;
    const q = searchModalItem.toLowerCase();
    return selectedPermohonan.items.filter((it) => {
      const kode = it.kode_item ? it.kode_item.toLowerCase() : '';
      const jenis = it.jenis_kategori?.nama_jenis ? it.jenis_kategori.nama_jenis.toLowerCase() : '';
      const kat = it.jenis_kategori?.kategori?.nama_kategori ? it.jenis_kategori.kategori.nama_kategori.toLowerCase() : '';
      const baki = it.nomor_rak ? it.nomor_rak.toString().toLowerCase() : '';
      return kode.includes(q) || jenis.includes(q) || kat.includes(q) || baki.includes(q);
    });
  }, [selectedPermohonan, searchModalItem]);

  const handleOpenVerification = (permohonan, action) => {
    setVerifyingPermohonan(permohonan);
    setActionType(action);
    setCatatanVerifikasi('');
    setIsOpenVerificationModal(true);
  };

  const handleSubmitVerification = (e) => {
    e.preventDefault();
    if (!verifyingPermohonan) return;

    if (actionType === 'ditolak' && !catatanVerifikasi.trim()) {
      toast.warning('Silakan sertakan alasan penolakan permohonan!');
      return;
    }

    setIsOpenVerificationModal(false);
    setShowPinModalVerification(true);
  };

  const executeVerification = async () => {
    setIsSubmitting(true);
    setShowPinModalVerification(false);
    try {
      const res = await http.post(
        `permohonan-brankas/${verifyingPermohonan.id}/verifikasi`,
        { action: actionType, catatan_verifikasi: catatanVerifikasi },
        { headers: { Authorization: getAuthorizationHeader() } }
      );

      if (res.data.status) {
        toast.success(actionType === 'disetujui' ? 'Permohonan disetujui! Barang resmi masuk ke Brankas Pusat.' : 'Permohonan berhasil ditolak.');
        fetchPermohonanList();
        window.dispatchEvent(new Event('refresh-pending-verification'));
      }
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || 'Gagal memproses verifikasi');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStatusBadge = (status, isCompact = false) => {
    switch (status) {
      case 'disetujui':
        return (
          <span className={`inline-flex items-center gap-1 font-bold rounded-lg border bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border-emerald-300 dark:border-emerald-700 ${isCompact ? 'px-2 py-0.5 text-[10px]' : 'px-2.5 py-1 text-xs'}`}>
            <IconCheck size={isCompact ? 12 : 14} /> Disetujui
          </span>
        );
      case 'ditolak':
        return (
          <span className={`inline-flex items-center gap-1 font-bold rounded-lg border bg-rose-50 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300 border-rose-300 dark:border-rose-700 ${isCompact ? 'px-2 py-0.5 text-[10px]' : 'px-2.5 py-1 text-xs'}`}>
            <IconX size={isCompact ? 12 : 14} /> Ditolak
          </span>
        );
      case 'dibatalkan':
        return (
          <span className={`inline-flex items-center gap-1 font-bold rounded-lg border bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-300 dark:border-slate-700 ${isCompact ? 'px-2 py-0.5 text-[10px]' : 'px-2.5 py-1 text-xs'}`}>
            <IconX size={isCompact ? 12 : 14} /> Dibatalkan
          </span>
        );
      default:
        return (
          <span className={`inline-flex items-center gap-1 font-bold rounded-lg border bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 border-amber-300 dark:border-amber-700 ${isCompact ? 'px-2 py-0.5 text-[10px]' : 'px-2.5 py-1 text-xs'}`}>
            <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-ping"></span>
            Menunggu Verifikasi
          </span>
        );
    }
  };

  const activeFilters = useMemo(() => {
    const list = [];
    if (statusFilter) {
      const statusMap = {
        pending: 'Menunggu Verifikasi',
        disetujui: 'Disetujui',
        ditolak: 'Ditolak',
        dibatalkan: 'Dibatalkan',
      };
      list.push({
        id: 'status',
        label: `Status: ${statusMap[statusFilter] || statusFilter}`,
        icon: '📋',
        onClear: () => {
          setStatusFilter('');
          setPage(1);
        },
      });
    }
    if (startDate && endDate) {
      list.push({
        id: 'tanggal',
        label: awal === akhir ? `Tanggal: ${awal}` : `Tanggal: ${awal} - ${akhir}`,
        icon: '📅',
        onClear: () => {
          setStartDate(null);
          setEndDate(null);
          setPage(1);
        },
      });
    }
    if (selectedCabang) {
      const namaCabang = cabangList.find((c) => String(c.id) === String(selectedCabang))?.nama_cabang || 'Cabang';
      list.push({
        id: 'cabang',
        label: `Asal: ${namaCabang}`,
        icon: '🏪',
        onClear: () => {
          setSelectedCabang('');
          setPage(1);
        },
      });
    }
    if (selectedUser) {
      const namaUser = userList.find((u) => String(u.id) === String(selectedUser))?.name || 'Operator';
      list.push({
        id: 'user',
        label: `Pemohon: ${namaUser}`,
        icon: '👤',
        onClear: () => {
          setSelectedUser('');
          setPage(1);
        },
      });
    }
    return list;
  }, [statusFilter, startDate, endDate, awal, akhir, selectedCabang, cabangList, selectedUser, userList]);

  const activeFilterCount = activeFilters.length;
  const hasActiveFilters = activeFilterCount > 0 || Boolean(search);

  const resetAllFilters = () => {
    setStatusFilter('');
    setSearch('');
    setSelectedCabang('');
    setSelectedUser('');
    setStartDate(null);
    setEndDate(null);
    setPage(1);
  };

  return (
    <Layouts 
    //title="Verifikasi Permohonan Brankas Pusat (V2)"
    >
      <div className="p-3 sm:p-4 lg:p-6 w-full space-y-4 min-h-[85vh]">

        {/* Top Header Card */}
        <div className="bg-white dark:bg-slate-900 p-4 sm:p-5 rounded-md border border-slate-200/80 dark:border-slate-800 shadow-sm">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 pb-3 border-b border-slate-100 dark:border-slate-800">
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-lg sm:text-xl font-bold text-slate-800 dark:text-slate-100">
                  Brankas Pusat
                </h1>
              </div>
            </div>

            {/* Quick Actions & View Mode Toggle */}
            <div className="flex items-center gap-2 self-end md:self-center">
              {/* View Toggle (Cards vs Table) */}
              <div className="flex items-center bg-slate-100 dark:bg-slate-800 p-1 rounded-xl border border-slate-200/70 dark:border-slate-700">
                <button
                  type="button"
                  onClick={() => setViewMode('cards')}
                  className={`p-1.5 rounded-lg text-xs font-semibold flex items-center gap-1 transition-all ${
                    viewMode === 'cards'
                      ? 'bg-white dark:bg-slate-700 text-amber-600 dark:text-amber-400 shadow-sm'
                      : 'text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'
                  }`}
                  title="Tampilan Kartu (Rekomendasi HP/Tablet)"
                >
                  <IconLayoutGrid size={17} />
                  <span className="hidden sm:inline">Kartu</span>
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode('table')}
                  className={`p-1.5 rounded-lg text-xs font-semibold flex items-center gap-1 transition-all ${
                    viewMode === 'table'
                      ? 'bg-white dark:bg-slate-700 text-amber-600 dark:text-amber-400 shadow-sm'
                      : 'text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'
                  }`}
                  title="Tampilan Tabel"
                >
                  <IconTable size={17} />
                  <span className="hidden sm:inline">Tabel</span>
                </button>
              </div>

              {/* Refresh Button */}
              <button
                type="button"
                onClick={fetchPermohonanList}
                disabled={loading}
                className="p-2 text-slate-600 dark:text-slate-300 hover:text-amber-600 dark:hover:text-amber-400 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-xl transition-all"
                title="Muat Ulang"
              >
                <IconReload size={18} className={loading ? 'animate-spin text-amber-500' : ''} />
              </button>
            </div>
          </div>

          {/* Search & Filter Trigger Bar */}
          <div className="flex items-center gap-2 pt-3">
            {/* Search Input */}
            <div className="relative flex-1">
              <IconSearch size={17} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Cari No. Request permohonan..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                className="w-full pl-10 pr-9 py-2 sm:py-2.5 text-xs sm:text-sm bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-800 dark:text-slate-100 placeholder:text-slate-400 outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 transition-all"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-0.5"
                >
                  <IconX size={15} />
                </button>
              )}
            </div>

            {/* Filter Toggle Button */}
            <button
              type="button"
              onClick={() => setIsFilterOpen(true)}
              className={`flex items-center gap-1.5 px-3 py-2 sm:py-2.5 rounded-xl text-xs sm:text-sm font-semibold border transition-all shrink-0 cursor-pointer shadow-xs ${
                isFilterOpen || activeFilterCount > 0
                  ? 'bg-amber-500 border-amber-600 text-white shadow-amber-500/20'
                  : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:border-amber-400'
              }`}
              title="Buka Filter"
            >
              <IconAdjustmentsHorizontal size={17} />
              <span>Filter</span>
              {activeFilterCount > 0 && (
                <span
                  className={`px-1.5 py-0.2 text-[10px] font-bold rounded-full leading-tight ${
                    isFilterOpen || activeFilterCount > 0
                      ? 'bg-white text-amber-700'
                      : 'bg-amber-500 text-white'
                  }`}
                >
                  {activeFilterCount}
                </span>
              )}
            </button>
          </div>

          {/* Horizontal Status Chips (Touch friendly pill bar) */}
          <div className="flex items-center gap-1.5 overflow-x-auto pt-2.5 pb-1 scrollbar-none">
            {[
              { label: 'Semua Status', value: '' },
              { label: 'Menunggu Verifikasi', value: 'pending', badgeColor: 'bg-amber-500' },
              { label: 'Disetujui', value: 'disetujui', badgeColor: 'bg-emerald-500' },
              { label: 'Ditolak', value: 'ditolak', badgeColor: 'bg-rose-500' },
              { label: 'Dibatalkan', value: 'dibatalkan', badgeColor: 'bg-slate-400' },
            ].map((chip) => {
              const isActive = statusFilter === chip.value;
              return (
                <button
                  key={chip.value}
                  type="button"
                  onClick={() => {
                    setStatusFilter(chip.value);
                    setPage(1);
                  }}
                  className={`px-3 py-1.5 text-xs font-bold rounded-xl whitespace-nowrap transition-all flex items-center gap-1.5 cursor-pointer flex-shrink-0 ${
                    isActive
                      ? 'bg-amber-500 text-white shadow-sm shadow-amber-500/20'
                      : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'
                  }`}
                >
                  {chip.badgeColor && (
                    <span className={`w-2 h-2 rounded-full ${isActive ? 'bg-white' : chip.badgeColor}`}></span>
                  )}
                  <span>{chip.label}</span>
                </button>
              );
            })}
          </div>

          {/* Active Filter Chips (Pills horizontal bar) */}
          {activeFilterCount > 0 && (
            <div className="flex items-center gap-1.5 overflow-x-auto pt-2 pb-0.5 w-full max-w-full no-scrollbar text-xs">
              <span className="text-[11px] font-semibold text-slate-400 dark:text-slate-500 shrink-0 uppercase tracking-wider">
                Filter:
              </span>
              {activeFilters.map((flt) => (
                <div
                  key={flt.id}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800/60 text-amber-800 dark:text-amber-300 font-medium whitespace-nowrap shadow-2xs shrink-0"
                >
                  <span className="text-xs">{flt.icon}</span>
                  <span className="max-w-[150px] sm:max-w-[200px] truncate">{flt.label}</span>
                  <button
                    type="button"
                    onClick={flt.onClear}
                    className="p-0.5 hover:bg-amber-200/60 dark:hover:bg-amber-800/60 rounded-full transition-colors ml-0.5 cursor-pointer text-amber-600 dark:text-amber-400"
                    title={`Hapus filter ${flt.label}`}
                  >
                    <IconX size={12} />
                  </button>
                </div>
              ))}

              <button
                type="button"
                onClick={resetAllFilters}
                className="text-[11px] font-bold text-rose-600 dark:text-rose-400 hover:underline px-2 py-0.5 rounded shrink-0 cursor-pointer whitespace-nowrap"
              >
                Reset Semua
              </button>
            </div>
          )}

          {/* ═══ Filter Slide-over Drawer (Kanan Layar) ═══ */}
          <Transition appear show={isFilterOpen} as={Fragment}>
            <Dialog
              as="div"
              className="relative z-[99999]"
              onClose={() => setIsFilterOpen(false)}
            >
              {/* Backdrop Overlay Tipis & Tanpa Blur (Data di samping tetap tajam dan terbaca jelas) */}
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <div className="fixed inset-0 bg-slate-900/15 dark:bg-black/30 pointer-events-auto" />
              </Transition.Child>

              {/* Drawer Container */}
              <div className="fixed inset-0 overflow-hidden pointer-events-none">
                <div className="fixed inset-y-0 right-0 flex max-w-full pl-0 sm:pl-10">
                  <Transition.Child
                    as={Fragment}
                    enter="transform transition ease-in-out duration-300 sm:duration-400"
                    enterFrom="translate-x-full"
                    enterTo="translate-x-0"
                    leave="transform transition ease-in-out duration-300 sm:duration-400"
                    leaveFrom="translate-x-0"
                    leaveTo="translate-x-full"
                  >
                    <Dialog.Panel className="pointer-events-auto w-screen max-w-full sm:max-w-md md:max-w-lg transform bg-white dark:bg-slate-900 shadow-2xl border-l border-slate-200 dark:border-slate-800 text-left align-middle transition-all flex flex-col h-screen max-h-screen">
                      {/* Drawer Header */}
                      <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 flex items-center justify-between shrink-0 sticky top-0 z-20">
                        <div className="flex items-center gap-2.5">
                          <div className="p-2 rounded-xl bg-amber-500/10 text-amber-600 dark:text-amber-400">
                            <IconAdjustmentsHorizontal size={20} />
                          </div>
                          <div>
                            <Dialog.Title as="h2" className="text-base font-bold text-slate-800 dark:text-slate-100">
                              Filter Permohonan
                            </Dialog.Title>
                            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                              {activeFilterCount > 0
                                ? `${activeFilterCount} kriteria filter aktif`
                                : 'Sesuaikan pencarian permohonan'}
                            </p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => setIsFilterOpen(false)}
                          className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 transition cursor-pointer"
                        >
                          <IconX size={18} />
                        </button>
                      </div>

                      {/* Drawer Body */}
                      <div className="p-5 flex-1 bg-slate-50/50 dark:bg-slate-900/50 overflow-y-auto space-y-4">
                        {/* 1. Filter Tanggal Permohonan */}
                        <div className="space-y-1.5">
                          <label className="block text-xs font-bold text-slate-700 dark:text-slate-300">
                            Rentang Tanggal Permohonan
                          </label>
                          <div className="dropdown dropdown-bottom w-full">
                            <div
                              tabIndex={0}
                              className="w-full h-10 border border-slate-200 dark:border-slate-600 rounded-xl px-3 text-xs sm:text-sm text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-800 cursor-pointer hover:border-amber-400 dark:hover:border-amber-500 flex items-center justify-between gap-1.5 shadow-xs whitespace-nowrap"
                              title="Filter Tanggal"
                            >
                              <div className="flex items-center gap-2 truncate">
                                <IconCalendar size={16} className="text-amber-500 shrink-0" />
                                <span className="truncate">
                                  {startDate && endDate && awal && awal !== 'Invalid date'
                                    ? awal === akhir
                                      ? awal
                                      : `${awal} - ${akhir}`
                                    : 'Pilih Rentang Tanggal'}
                                </span>
                              </div>
                              <IconChevronDown size={14} className="text-slate-400 shrink-0" />
                            </div>
                            <div
                              tabIndex={0}
                              className="dropdown-content menu p-3 shadow-2xl bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 z-[9999] mt-1 left-0 max-w-[92vw] overflow-x-auto"
                            >
                              {/* Quick Presets */}
                              <div className="p-1.5 bg-slate-50 dark:bg-slate-900/50 rounded-xl border border-slate-100 dark:border-slate-700/60 flex flex-wrap items-center justify-center gap-1.5 mb-2">
                                <button
                                  type="button"
                                  onClick={() => setQuickDate('today')}
                                  className="px-2.5 py-1 text-[11px] font-semibold rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:border-amber-400 hover:text-amber-600 text-slate-600 dark:text-slate-300 transition-all cursor-pointer"
                                >
                                  Hari Ini
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setQuickDate('7days')}
                                  className="px-2.5 py-1 text-[11px] font-semibold rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:border-amber-400 hover:text-amber-600 text-slate-600 dark:text-slate-300 transition-all cursor-pointer"
                                >
                                  7 Hari Terakhir
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setQuickDate('30days')}
                                  className="px-2.5 py-1 text-[11px] font-semibold rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:border-amber-400 hover:text-amber-600 text-slate-600 dark:text-slate-300 transition-all cursor-pointer"
                                >
                                  30 Hari
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setQuickDate('thisMonth')}
                                  className="px-2.5 py-1 text-[11px] font-semibold rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:border-amber-400 hover:text-amber-600 text-slate-600 dark:text-slate-300 transition-all cursor-pointer"
                                >
                                  Bulan Ini
                                </button>
                              </div>

                              <DateRange
                                ranges={[selectionRange]}
                                onChange={handleSelectDate}
                                rangeColors={['#f59e0b']}
                                months={1}
                                direction="horizontal"
                                showDateDisplay={true}
                                moveRangeOnFirstSelection={false}
                              />

                              {startDate && (
                                <div className="p-2 border-t border-slate-200 dark:border-slate-700 flex justify-end">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setStartDate(null);
                                      setEndDate(null);
                                      setPage(1);
                                    }}
                                    className="text-xs text-rose-500 hover:underline font-bold cursor-pointer"
                                  >
                                    Reset Tanggal
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* 2. Filter Status Permohonan */}
                        <div className="space-y-1.5">
                          <label className="block text-xs font-bold text-slate-700 dark:text-slate-300">
                            Status Permohonan
                          </label>
                          <select
                            value={statusFilter}
                            onChange={(e) => {
                              setStatusFilter(e.target.value);
                              setPage(1);
                            }}
                            className="w-full h-10 border border-slate-200 dark:border-slate-600 rounded-xl px-3 text-xs sm:text-sm text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-800 cursor-pointer hover:border-amber-400 dark:hover:border-amber-500 outline-none font-medium shadow-xs"
                          >
                            <option value="">Semua Status</option>
                            <option value="pending">Menunggu Verifikasi</option>
                            <option value="disetujui">Disetujui</option>
                            <option value="ditolak">Ditolak</option>
                            <option value="dibatalkan">Dibatalkan</option>
                          </select>
                        </div>

                        {/* 3. Toko / Cabang Asal */}
                        <div className="space-y-1.5">
                          <label className="block text-xs font-bold text-slate-700 dark:text-slate-300">
                            Brankas / Toko Asal
                          </label>
                          <select
                            value={selectedCabang}
                            onChange={(e) => {
                              setSelectedCabang(e.target.value);
                              setPage(1);
                            }}
                            className="w-full h-10 border border-slate-200 dark:border-slate-600 rounded-xl px-3 text-xs sm:text-sm text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-800 cursor-pointer hover:border-amber-400 dark:hover:border-amber-500 outline-none font-medium shadow-xs"
                          >
                            <option value="">Semua Brankas Toko</option>
                            {cabangList.map((c) => (
                              <option key={c.id} value={c.id}>
                                {c.nama_cabang}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* 4. Operator Pemohon */}
                        <div className="space-y-1.5">
                          <label className="block text-xs font-bold text-slate-700 dark:text-slate-300">
                            Operator Pemohon
                          </label>
                          <select
                            value={selectedUser}
                            onChange={(e) => {
                              setSelectedUser(e.target.value);
                              setPage(1);
                            }}
                            className="w-full h-10 border border-slate-200 dark:border-slate-600 rounded-xl px-3 text-xs sm:text-sm text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-800 cursor-pointer hover:border-amber-400 dark:hover:border-amber-500 outline-none font-medium shadow-xs"
                          >
                            <option value="">Semua Operator Pemohon</option>
                            {userList.map((u) => (
                              <option key={u.id} value={u.id}>
                                {u.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {/* Drawer Footer */}
                      <div className="px-5 py-3.5 border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 flex items-center justify-between gap-3 shrink-0 sticky bottom-0 z-20">
                        <div className="text-xs text-slate-500 dark:text-slate-400">
                          Total:{' '}
                          <strong className="text-slate-700 dark:text-slate-200">
                            {totalPermohonan}
                          </strong>
                        </div>
                        <div className="flex items-center gap-2">
                          {activeFilterCount > 0 && (
                            <button
                              type="button"
                              onClick={resetAllFilters}
                              className="px-3 py-2 text-xs font-semibold text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/30 rounded-xl transition-colors cursor-pointer flex items-center gap-1"
                            >
                              <IconRotateClockwise size={14} />
                              Reset
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => setIsFilterOpen(false)}
                            className="px-4 py-2 text-xs font-bold bg-amber-500 hover:bg-amber-600 active:scale-95 text-white rounded-xl shadow-md transition-all cursor-pointer"
                          >
                            Tutup / Selesai
                          </button>
                        </div>
                      </div>
                    </Dialog.Panel>
                  </Transition.Child>
                </div>
              </div>
            </Dialog>
          </Transition>
        </div>

        {/* Content Container (Card Grid or Table) */}
        {loading ? (
          <div className="py-24 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200/80 dark:border-slate-800 shadow-sm flex flex-col items-center justify-center gap-3">
            <span className="loading loading-spinner loading-lg text-amber-500"></span>
            <p className="text-slate-400 text-xs font-semibold animate-pulse">Memuat permohonan brankas pusat...</p>
          </div>
        ) : permohonans.length === 0 ? (
          <div className="py-20 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200/80 dark:border-slate-800 shadow-sm flex flex-col items-center justify-center text-center p-6 space-y-3">
            <div className="w-16 h-16 bg-amber-50 dark:bg-slate-800 rounded-full flex items-center justify-center text-amber-500">
              <IconInbox size={32} />
            </div>
            <div>
              <h3 className="font-bold text-sm text-slate-800 dark:text-slate-200">
                Tidak Ada Permohonan Ditemukan
              </h3>
              <p className="text-xs text-slate-500 dark:text-slate-400 max-w-sm mt-1">
                {hasActiveFilters
                  ? 'Tidak ada data yang sesuai dengan parameter filter atau kata kunci pencarian Anda.'
                  : 'Belum ada data permohonan mutasi barang ke Brankas Pusat.'}
              </p>
            </div>
            {hasActiveFilters && (
              <div className="flex items-center gap-2 pt-1 flex-wrap justify-center">
                <button
                  type="button"
                  onClick={resetAllFilters}
                  className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold rounded-xl shadow-sm transition-all cursor-pointer"
                >
                  Reset Semua Filter
                </button>
              </div>
            )}
          </div>
        ) : viewMode === 'cards' ? (
          /* ========================================================================= */
          /* CARD FEED VIEW (Mobile & Tablet First)                                    */
          /* ========================================================================= */
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-3 sm:gap-4">
              {permohonans.map((p) => {
                const totalPcs = p.total_pcs || (p.items ? p.items.length : 0);
                const totalBerat = parseFloat(p.total_berat || 0).toFixed(3);
                const isPending = p.status === 'pending';

                return (
                  <div
                    key={p.id}
                    className={`bg-white dark:bg-slate-900 rounded-2xl p-4 sm:p-5 border transition-all duration-200 flex flex-col justify-between shadow-sm hover:shadow-md ${
                      isPending
                        ? 'border-amber-300/80 dark:border-amber-700/60 ring-1 ring-amber-400/20'
                        : 'border-slate-200/80 dark:border-slate-800'
                    }`}
                  >
                    {/* Card Top: Status, Request Number, Date */}
                    <div>
                      <div className="flex items-start justify-between gap-2 pb-2.5 border-b border-slate-100 dark:border-slate-800">
                        <div>
                          <div className="flex items-center gap-1.5">
                            <span className="font-mono font-extrabold text-sm sm:text-base text-slate-900 dark:text-slate-100 tracking-tight">
                              {p.no_permohonan}
                            </span>
                            <button
                              type="button"
                              onClick={(e) => handleCopyNoPermohonan(e, p.no_permohonan)}
                              className="text-slate-400 hover:text-amber-500 p-0.5 rounded transition-colors cursor-pointer"
                              title="Salin No. Request"
                            >
                              <IconCopy size={14} />
                            </button>
                          </div>
                          <p className="text-[11px] text-slate-400 dark:text-slate-500 mt-0.5 flex items-center gap-1">
                            <IconClock size={13} />
                            <span>
                              {new Date(p.tanggal || p.created_at).toLocaleDateString('id-ID', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </span>
                          </p>
                        </div>
                        <div>{renderStatusBadge(p.status, true)}</div>
                      </div>

                      {/* Card Body: Info Toko & Pemohon */}
                      <div className="py-3 space-y-2 text-xs">
                        <div className="flex items-center justify-between">
                          <span className="text-slate-500 dark:text-slate-400 flex items-center gap-1">
                            <IconBuildingStore size={15} className="text-amber-500" />
                            <span>Brankas Asal:</span>
                          </span>
                          <span className="font-bold text-slate-800 dark:text-slate-200 text-right">
                            {p.cabang_asal?.nama_cabang ? p.cabang_asal.nama_cabang.replace(/Etalase/gi, 'Brankas') : '-'}
                          </span>
                        </div>

                        <div className="flex items-center justify-between">
                          <span className="text-slate-500 dark:text-slate-400 flex items-center gap-1">
                            <IconUser size={15} className="text-amber-500" />
                            <span>Pemohon:</span>
                          </span>
                          <span className="font-semibold text-slate-700 dark:text-slate-300 text-right">
                            {p.user_pemohon?.name || '-'}
                          </span>
                        </div>

                        {/* Highlight Metrics Box (PCS & Berat) */}
                        <div className="mt-2.5 p-3 rounded-xl bg-gradient-to-r from-amber-50/90 to-amber-100/60 dark:from-amber-950/30 dark:to-slate-800/80 border border-amber-200/70 dark:border-amber-900/40 flex items-center justify-between">
                          <div>
                            <span className="text-[10px] font-bold uppercase tracking-wider text-amber-800 dark:text-amber-400 block">
                              Total Item
                            </span>
                            <span className="text-base font-extrabold text-slate-900 dark:text-slate-100">
                              {totalPcs} <span className="text-xs font-semibold text-slate-500">PCS</span>
                            </span>
                          </div>
                          <div className="text-right">
                            <span className="text-[10px] font-bold uppercase tracking-wider text-amber-800 dark:text-amber-400 block">
                              Total Berat
                            </span>
                            <span className="text-base font-extrabold text-amber-600 dark:text-amber-400 font-mono">
                              {totalBerat} <span className="text-xs font-semibold text-slate-500 font-sans">gr</span>
                            </span>
                          </div>
                        </div>

                        {/* Catatan Pemohon Snippet if any */}
                        {p.catatan_pemohon && (
                          <p className="text-[11px] text-slate-500 dark:text-slate-400 italic bg-slate-50 dark:bg-slate-800 p-2 rounded-lg border border-slate-100 dark:border-slate-700 line-clamp-2">
                            "{p.catatan_pemohon}"
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Card Footer: Action Buttons (Large touch targets for thumb) */}
                    <div className="pt-3 border-t border-slate-100 dark:border-slate-800 space-y-2">
                      {/* If status is pending, show primary Action Buttons */}
                      {isPending && (
                        <div className="grid grid-cols-2 gap-2">
                          <button
                            type="button"
                            onClick={() => handleOpenVerification(p, 'disetujui')}
                            className="w-full py-2.5 px-3 bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white text-xs font-bold rounded-xl shadow-sm transition-all flex items-center justify-center gap-1.5 cursor-pointer"
                          >
                            <IconCheck size={16} />
                            <span>Setujui</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => handleOpenVerification(p, 'ditolak')}
                            className="w-full py-2.5 px-3 bg-rose-600 hover:bg-rose-700 active:bg-rose-800 text-white text-xs font-bold rounded-xl shadow-sm transition-all flex items-center justify-center gap-1.5 cursor-pointer"
                          >
                            <IconX size={16} />
                            <span>Tolak</span>
                          </button>
                        </div>
                      )}

                      {/* Secondary Buttons: Detail & Foto */}
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => handleOpenDetail(p.id)}
                          className="flex-1 py-2 px-3 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 text-xs font-semibold rounded-xl transition-all flex items-center justify-center gap-1.5 cursor-pointer"
                        >
                          <IconEye size={15} />
                          <span>Detail Item</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => handleOpenImageModal(p)}
                          className="py-2 px-3 bg-amber-50 hover:bg-amber-100 dark:bg-amber-950/30 dark:hover:bg-amber-900/40 text-amber-700 dark:text-amber-400 text-xs font-semibold rounded-xl border border-amber-200 dark:border-amber-800 transition-all flex items-center justify-center gap-1 cursor-pointer"
                          title="Lihat Galeri Foto"
                        >
                          <IconPhoto size={15} />
                          <span className="hidden sm:inline">Foto</span>
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          /* ========================================================================= */
          /* TABLE VIEW (Alternative toggle)                                           */
          /* ========================================================================= */
          <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200/80 dark:border-slate-800 shadow-sm overflow-x-auto">
            <table className="w-full text-xs text-left text-slate-600 dark:text-slate-300">
              <thead className="bg-amber-50 dark:bg-slate-800/80 text-amber-800 dark:text-amber-300 border-b border-amber-200/70 dark:border-slate-700 text-[11px] font-bold uppercase tracking-wider">
                <tr>
                  <th className="p-3.5">No.</th>
                  <th className="p-3.5">No. Request</th>
                  <th className="p-3.5">Tgl Request</th>
                  <th className="p-3.5">Brankas Asal</th>
                  <th className="p-3.5">Pemohon</th>
                  <th className="p-3.5 text-center">Total Barang</th>
                  <th className="p-3.5">Status</th>
                  <th className="p-3.5 text-center">Aksi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {permohonans.map((p, index) => (
                  <tr
                    key={p.id}
                    onClick={() => handleOpenDetail(p.id)}
                    className="hover:bg-amber-50/30 dark:hover:bg-slate-800/40 transition-colors cursor-pointer"
                  >
                    <td className="p-3.5 text-slate-500 font-medium">{(page - 1) * 12 + index + 1}.</td>
                    <td className="p-3.5 font-mono font-bold text-slate-800 dark:text-slate-100">{p.no_permohonan}</td>
                    <td className="p-3.5 text-slate-500">
                      {new Date(p.tanggal || p.created_at).toLocaleDateString('id-ID', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </td>
                    <td className="p-3.5 font-semibold text-slate-800 dark:text-slate-200">
                      {p.cabang_asal?.nama_cabang ? p.cabang_asal.nama_cabang.replace(/Etalase/gi, 'Brankas') : '-'}
                    </td>
                    <td className="p-3.5">{p.user_pemohon?.name || '-'}</td>
                    <td className="p-3.5 text-center font-bold text-slate-800 dark:text-slate-100">
                      {p.total_pcs} PCS ({parseFloat(p.total_berat).toFixed(3)} gr)
                    </td>
                    <td className="p-3.5">{renderStatusBadge(p.status, true)}</td>
                    <td className="p-3.5 text-center" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => handleOpenDetail(p.id)}
                          className="p-1.5 text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/30 rounded-lg transition-colors cursor-pointer"
                          title="Lihat Detail"
                        >
                          <IconEye size={16} />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleOpenImageModal(p)}
                          className="p-1.5 text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/30 rounded-lg transition-colors cursor-pointer"
                          title="Lihat Foto"
                        >
                          <IconPhoto size={16} />
                        </button>
                        {p.status === 'pending' && (
                          <>
                            <button
                              type="button"
                              onClick={() => handleOpenVerification(p, 'disetujui')}
                              className="px-2.5 py-1 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-[11px] rounded-lg shadow-sm cursor-pointer"
                            >
                              Setujui
                            </button>
                            <button
                              type="button"
                              onClick={() => handleOpenVerification(p, 'ditolak')}
                              className="px-2.5 py-1 bg-rose-600 hover:bg-rose-700 text-white font-bold text-[11px] rounded-lg shadow-sm cursor-pointer"
                            >
                              Tolak
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination Controls */}
        {!loading && permohonans.length > 0 && (
          <div className="flex flex-col sm:flex-row justify-between items-center gap-3 p-4 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200/80 dark:border-slate-800 shadow-sm">
            <span className="text-xs text-slate-500 dark:text-slate-400">
              Menampilkan <span className="font-bold text-slate-800 dark:text-slate-100">{permohonans.length}</span> dari{' '}
              <span className="font-bold text-slate-800 dark:text-slate-100">{totalPermohonan}</span> permohonan
            </span>

            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1">
                Hal.
                <input
                  type="text"
                  value={pageInput}
                  onChange={(e) => setPageInput(e.target.value)}
                  onBlur={jumpToPage}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      jumpToPage();
                      e.target.blur();
                    }
                  }}
                  className="w-11 h-8 text-center border border-slate-200 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-800 text-amber-600 dark:text-amber-400 font-bold focus:border-amber-400 outline-none text-xs"
                />
                dari <strong className="text-slate-700 dark:text-slate-200">{lastPage}</strong>
              </span>

              <button
                type="button"
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                disabled={page <= 1}
                className="w-8 h-8 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center transition cursor-pointer"
              >
                <IconChevronLeft size={16} />
              </button>
              <button
                type="button"
                onClick={() => setPage((prev) => Math.min(lastPage, prev + 1))}
                disabled={page >= lastPage}
                className="w-8 h-8 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center transition cursor-pointer"
              >
                <IconChevronRight size={16} />
              </button>
            </div>
          </div>
        )}



        {/* ========================================================================= */}
        {/* DETAIL MODAL (Responsive Full/Bottom Sheet for Mobile, Modal for Tablet)   */}
        {/* ========================================================================= */}
        {isOpenDetailModal && selectedPermohonan && (
          <div
            className="fixed inset-0 z-[999] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
            onClick={() => setIsOpenDetailModal(false)}
          >
            <div
              className="bg-white dark:bg-slate-900 w-full sm:max-w-4xl lg:max-w-5xl rounded-t-3xl sm:rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 max-h-[94vh] flex flex-col overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Drag bar for mobile bottom sheet */}
              <div className="w-12 h-1.5 bg-slate-300 dark:bg-slate-700 rounded-full mx-auto mt-2.5 sm:hidden"></div>

              {/* Modal Header */}
              <div className="p-4 sm:p-5 pb-3 border-b border-slate-100 dark:border-slate-800 flex items-start justify-between gap-2 flex-shrink-0">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="text-base sm:text-lg font-bold text-slate-900 dark:text-slate-100 font-mono">
                      {selectedPermohonan.no_permohonan}
                    </h2>
                    {renderStatusBadge(selectedPermohonan.status, true)}
                  </div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                    {selectedPermohonan.cabang_asal?.nama_cabang ? selectedPermohonan.cabang_asal.nama_cabang.replace(/Etalase/gi, 'Brankas') : '-'} • Oleh{' '}
                    <strong className="text-slate-700 dark:text-slate-300">{selectedPermohonan.user_pemohon?.name || '-'}</strong>
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => setIsOpenDetailModal(false)}
                  className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer"
                >
                  <IconX size={20} />
                </button>
              </div>

              {/* Tab Navigation (Segmented touch bar) */}
              <div className="flex border-b border-slate-200 dark:border-slate-800 gap-1 px-4 pt-1 overflow-x-auto scrollbar-none flex-shrink-0 bg-slate-50/50 dark:bg-slate-900/50">
                {[
                  { key: 'ringkasan', label: 'Ringkasan', icon: IconChartPie },
                  { key: 'list', label: `Daftar Item (${selectedPermohonan.items?.length || 0})`, icon: IconListDetails },
                  { key: 'operator', label: 'Operator', icon: IconUser },
                  { key: 'verifikator', label: 'Verifikator', icon: IconCircleCheck },
                  { key: 'galeri', label: 'Galeri Foto', icon: IconPhoto },
                ].map((tab) => {
                  const Icon = tab.icon;
                  const isActive = modalTab === tab.key;
                  return (
                    <button
                      key={tab.key}
                      type="button"
                      onClick={() => setModalTab(tab.key)}
                      className={`px-3 py-2 text-xs font-bold flex items-center gap-1.5 border-b-2 transition-all whitespace-nowrap cursor-pointer ${
                        isActive
                          ? 'border-amber-500 text-amber-600 dark:text-amber-400 bg-amber-50/60 dark:bg-amber-950/20'
                          : 'border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'
                      }`}
                    >
                      <Icon size={15} />
                      <span>{tab.label}</span>
                    </button>
                  );
                })}
              </div>

              {/* Scrollable Tab Content */}
              <div className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-4">
                {/* 1. Tab Ringkasan */}
                {modalTab === 'ringkasan' && (() => {
                  const itemsList = selectedPermohonan.items || [];
                  const totalPcsVal = selectedPermohonan.total_pcs || itemsList.length;
                  const totalBeratVal = parseFloat(selectedPermohonan.total_berat || itemsList.reduce((acc, it) => acc + parseFloat(it.berat || it.item?.berat || 0), 0));
                  const avgBeratVal = itemsList.length > 0 ? (totalBeratVal / itemsList.length) : (totalPcsVal > 0 ? totalBeratVal / totalPcsVal : 0);
                  const validModalItems = itemsList.filter(it => Number(it.harga_modal || it.item?.harga_modal || 0) > 0);
                  const avgModalVal = validModalItems.length > 0
                    ? Math.round(validModalItems.reduce((acc, it) => acc + Number(it.harga_modal || it.item?.harga_modal || 0), 0) / validModalItems.length)
                    : 0;
                  const maxBeratVal = itemsList.length > 0 ? Math.max(...itemsList.map(it => parseFloat(it.berat || it.item?.berat || 0) || 0)) : 0;
                  const maxModalVal = itemsList.length > 0 ? Math.max(...itemsList.map(it => Number(it.harga_modal || it.item?.harga_modal || 0) || 0)) : 0;

                  return (
                    <div className="space-y-4">
                      {/* Key Stats Cards (7 Cards) */}
                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5 sm:gap-3">
                        <div className="bg-amber-50/70 dark:bg-amber-950/20 border border-amber-200/70 dark:border-amber-900/40 rounded-xl p-3">
                          <span className="text-amber-800 dark:text-amber-400 text-[10px] font-bold uppercase tracking-wider block">Total Barang</span>
                          <div className="text-lg font-extrabold text-slate-900 dark:text-slate-100 mt-0.5">
                            {totalPcsVal} <span className="text-xs font-semibold text-slate-500">Pcs</span>
                          </div>
                        </div>

                        <div className="bg-amber-50/70 dark:bg-amber-950/20 border border-amber-200/70 dark:border-amber-900/40 rounded-xl p-3">
                          <span className="text-amber-800 dark:text-amber-400 text-[10px] font-bold uppercase tracking-wider block">Total Berat</span>
                          <div className="text-lg font-extrabold text-amber-600 dark:text-amber-400 mt-0.5 font-mono">
                            {totalBeratVal.toFixed(3)} <span className="text-xs font-semibold text-slate-500 font-sans">gr</span>
                          </div>
                        </div>

                        <div className="bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-3">
                          <span className="text-slate-500 text-[10px] font-bold uppercase tracking-wider block">Total Kategori</span>
                          <div className="text-lg font-extrabold text-slate-800 dark:text-slate-100 mt-0.5">
                            {Object.keys(detailSummary).length} <span className="text-xs font-semibold text-slate-500">Kategori</span>
                          </div>
                        </div>

                        <div className="bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-3">
                          <span className="text-slate-500 text-[10px] font-bold uppercase tracking-wider block">Rata-rata Berat</span>
                          <div className="text-lg font-extrabold text-slate-800 dark:text-slate-100 mt-0.5 font-mono">
                            {avgBeratVal.toFixed(3)} <span className="text-xs font-semibold text-slate-500 font-sans">gr</span>
                          </div>
                        </div>

                        <div className="bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-3">
                          <span className="text-slate-500 text-[10px] font-bold uppercase tracking-wider block">Rata-rata Modal</span>
                          <div className="text-lg font-extrabold text-slate-800 dark:text-slate-100 mt-0.5 font-mono">
                            Rp {avgModalVal.toLocaleString('id-ID')}
                          </div>
                        </div>

                        <div className="bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-3">
                          <span className="text-slate-500 text-[10px] font-bold uppercase tracking-wider block">Berat Maksimal</span>
                          <div className="text-lg font-extrabold text-slate-800 dark:text-slate-100 mt-0.5 font-mono">
                            {maxBeratVal.toFixed(3)} <span className="text-xs font-semibold text-slate-500 font-sans">gr</span>
                          </div>
                        </div>

                        <div className="bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-3">
                          <span className="text-slate-500 text-[10px] font-bold uppercase tracking-wider block">Modal Maksimal</span>
                          <div className="text-lg font-extrabold text-slate-800 dark:text-slate-100 mt-0.5 font-mono">
                            Rp {maxModalVal.toLocaleString('id-ID')}
                          </div>
                        </div>
                      </div>

                    {/* Breakdown Kategori & Karat */}
                    {Object.keys(detailSummary).length > 0 && (
                      <div className="space-y-3 p-3.5 bg-amber-50/50 dark:bg-amber-950/20 border border-amber-200/60 dark:border-amber-900/30 rounded-xl">
                        <h4 className="text-xs font-bold text-amber-800 dark:text-amber-400 uppercase tracking-wider flex items-center gap-1.5">
                          <IconChartPie size={16} />
                          <span>Breakdown Kategori & Karat</span>
                        </h4>
                        <div className="space-y-2">
                          {Object.values(detailSummary).map((cat, idx) => (
                            <div key={idx} className="bg-white dark:bg-slate-800 p-3 rounded-xl border border-amber-200/50 dark:border-slate-700 shadow-sm">
                              <div className="flex justify-between items-center mb-2 pb-2 border-b border-slate-100 dark:border-slate-700">
                                <span className="font-bold text-slate-800 dark:text-slate-100 text-sm">{cat.name}</span>
                                <span className="font-bold text-amber-600 dark:text-amber-400 text-xs bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 px-2.5 py-0.5 rounded-md">
                                  {cat.pcs} Pcs ({cat.berat.toFixed(3)} gr)
                                </span>
                              </div>
                              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                                {Object.entries(cat.byKarat).map(([karat, val], i) => (
                                  <div key={i} className="flex items-center justify-between bg-slate-50 dark:bg-slate-700/40 px-2.5 py-1.5 rounded-lg text-xs border border-slate-100 dark:border-slate-700">
                                    <span className="font-semibold text-slate-500 dark:text-slate-400">{karat} K</span>
                                    <span className="font-bold text-slate-800 dark:text-slate-100">{val.pcs} Pcs / {val.berat.toFixed(3)}g</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Catatan Area */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="p-3.5 bg-slate-50 dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 space-y-1">
                        <span className="text-slate-400 text-[10px] font-bold uppercase tracking-wider block">Catatan Pemohon</span>
                        <p className="text-xs text-slate-700 dark:text-slate-300 font-medium">
                          {selectedPermohonan.catatan_pemohon || <span className="italic text-slate-400">Tidak ada catatan</span>}
                        </p>
                      </div>
                      <div className="p-3.5 bg-slate-50 dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 space-y-1">
                        <span className="text-slate-400 text-[10px] font-bold uppercase tracking-wider block">
                          {selectedPermohonan.status === 'dibatalkan' ? 'Alasan Pembatalan' : 'Catatan Verifikator'}
                        </span>
                        <p className="text-xs text-slate-700 dark:text-slate-300 font-medium">
                          {((selectedPermohonan.catatan_verifikasi || selectedPermohonan.catatan_verifikator || selectedPermohonan.alasan_batal)
                            ? (selectedPermohonan.catatan_verifikasi || selectedPermohonan.catatan_verifikator || selectedPermohonan.alasan_batal).replace(/^\[BATAL\]\s*/i, '')
                            : null) || (
                            <span className="italic text-slate-400">
                              {selectedPermohonan.status === 'pending' ? 'Menunggu verifikasi...' : 'Tidak ada catatan'}
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })()}

                {/* 2. Tab Daftar Item (Responsive Cards on mobile, Table on tablet) */}
                {modalTab === 'list' && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between gap-2">
                      <div className="relative flex-1 max-w-xs">
                        <IconSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={15} />
                        <input
                          type="text"
                          placeholder="Cari kode, jenis, baki..."
                          value={searchModalItem}
                          onChange={(e) => setSearchModalItem(e.target.value)}
                          className="w-full pl-8 pr-3 py-1.5 text-xs bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg outline-none focus:ring-1 focus:ring-amber-500"
                        />
                      </div>
                      <span className="text-xs text-slate-500 font-semibold">
                        {filteredModalItems.length} item
                      </span>
                    </div>

                    {/* Mobile Items List (Cards) */}
                    <div className="sm:hidden space-y-2">
                      {filteredModalItems.length === 0 ? (
                        <p className="py-8 text-center text-xs text-slate-400">Tidak ada item ditemukan</p>
                      ) : (
                        filteredModalItems.map((it) => (
                          <div key={it.id} className="p-3 bg-slate-50 dark:bg-slate-800/80 rounded-xl border border-slate-200/80 dark:border-slate-700 flex items-center justify-between gap-2">
                            <div>
                              <div className="flex items-center gap-1.5">
                                <span className="text-xs font-mono font-bold text-amber-600 dark:text-amber-400">{it.kode_item}</span>
                                <span className="text-[10px] px-1.5 py-0.2 font-bold bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300 rounded">
                                  {it.karat} K
                                </span>
                              </div>
                              <p className="text-xs font-semibold text-slate-800 dark:text-slate-200 mt-0.5">
                                {it.jenis_kategori?.nama_jenis || '-'}
                              </p>
                              <p className="text-[10px] text-slate-400">
                                Baki/Rak: {it.nomor_rak || '-'}
                              </p>
                            </div>
                            <div className="text-right">
                              <span className="text-xs font-bold text-slate-900 dark:text-slate-100 font-mono">
                                {parseFloat(it.berat || 0).toFixed(3)} gr
                              </span>
                            </div>
                          </div>
                        ))
                      )}
                    </div>

                    {/* Tablet & Desktop Table */}
                    <div className="hidden sm:block border border-slate-200 dark:border-slate-700 rounded-xl overflow-x-auto max-h-80">
                      <table className="w-full text-xs text-left text-slate-600 dark:text-slate-300">
                        <thead className="bg-amber-50 dark:bg-slate-800 text-[11px] text-amber-800 dark:text-amber-300 uppercase border-b border-amber-200 dark:border-slate-700 sticky top-0 z-10 font-bold">
                          <tr>
                            <th className="p-2.5 w-10 text-center">No</th>
                            <th className="p-2.5">Kode Barang</th>
                            <th className="p-2.5">Nama Barang</th>
                            <th className="p-2.5 text-center">Baki / Rak</th>
                            <th className="p-2.5 text-right">Berat (gr)</th>
                            <th className="p-2.5 text-center">Karat</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                          {filteredModalItems.length === 0 ? (
                            <tr>
                              <td colSpan={6} className="text-center py-6 text-slate-400">
                                Tidak ada item yang sesuai dengan pencarian
                              </td>
                            </tr>
                          ) : (
                            filteredModalItems.map((it, idx) => (
                              <tr key={it.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/40">
                                <td className="p-2.5 text-slate-500 font-medium text-center">{idx + 1}.</td>
                                <td className="p-2.5 font-mono font-bold text-amber-600 dark:text-amber-400">{it.kode_item}</td>
                                <td className="p-2.5">{it.jenis_kategori?.nama_jenis || '-'}</td>
                                <td className="p-2.5 text-center font-medium">{it.nomor_rak || '-'}</td>
                                <td className="p-2.5 text-right font-bold text-slate-800 dark:text-slate-100">{parseFloat(it.berat).toFixed(3)} gr</td>
                                <td className="p-2.5 text-center font-semibold">{it.karat} K</td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* 3. Tab Operator */}
                {modalTab === 'operator' && (
                  <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 space-y-3 text-xs">
                    <div className="flex items-center gap-3 pb-3 border-b border-slate-200 dark:border-slate-700">
                      <div className="w-10 h-10 rounded-full bg-amber-500/10 text-amber-600 flex items-center justify-center font-bold text-base">
                        {selectedPermohonan.user_pemohon?.name ? selectedPermohonan.user_pemohon.name.charAt(0).toUpperCase() : <IconUser size={20} />}
                      </div>
                      <div>
                        <h4 className="font-bold text-sm text-slate-800 dark:text-slate-100">
                          {selectedPermohonan.user_pemohon?.name || 'Operator Rumah'}
                        </h4>
                        {selectedPermohonan.user_pemohon?.email && (
                          <p className="text-[11px] text-slate-400">
                            {selectedPermohonan.user_pemohon.email}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                      <div>
                        <span className="text-slate-400 text-[11px] block">Brankas / Toko Asal</span>
                        <p className="font-bold text-slate-800 dark:text-slate-200">
                          {selectedPermohonan.cabang_asal?.nama_cabang ? selectedPermohonan.cabang_asal.nama_cabang.replace(/Etalase/gi, 'Brankas') : '-'}
                        </p>
                      </div>
                      <div>
                        <span className="text-slate-400 text-[11px] block">Waktu Pengajuan</span>
                        <p className="font-semibold text-slate-700 dark:text-slate-300">
                          {moment(selectedPermohonan.created_at).format('DD MMM YYYY, HH:mm')}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* 4. Tab Verifikator */}
                {modalTab === 'verifikator' && (
                  <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 space-y-3 text-xs">
                    {selectedPermohonan.user_verifikator && selectedPermohonan.status !== 'dibatalkan' ? (
                      <>
                        <div className="flex items-center gap-3 pb-3 border-b border-slate-200 dark:border-slate-700">
                          <div className="w-10 h-10 rounded-full bg-emerald-500/10 text-emerald-600 flex items-center justify-center font-bold text-base">
                            <IconCircleCheck size={20} />
                          </div>
                          <div>
                            <h4 className="font-bold text-sm text-slate-800 dark:text-slate-100">
                              {selectedPermohonan.user_verifikator.name}
                            </h4>
                            {selectedPermohonan.user_verifikator?.email && (
                              <p className="text-[11px] text-slate-400">
                                {selectedPermohonan.user_verifikator.email}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                          <div>
                            <span className="text-slate-400 text-[11px] block">Waktu Selesai Verifikasi</span>
                            <p className="font-semibold text-slate-700 dark:text-slate-300">
                              {moment(selectedPermohonan.updated_at).format('DD MMM YYYY, HH:mm:ss')}
                            </p>
                          </div>
                          <div>
                            <span className="text-slate-400 text-[11px] block">Catatan Verifikator</span>
                            <p className="font-medium text-slate-700 dark:text-slate-300">
                              {selectedPermohonan.catatan_verifikasi || '-'}
                            </p>
                          </div>
                        </div>
                      </>
                    ) : selectedPermohonan.status === 'dibatalkan' ? (
                      <p className="text-center py-6 text-slate-500">Permohonan dibatalkan sebelum diverifikasi.</p>
                    ) : (
                      <p className="text-center py-6 text-amber-600 font-semibold">Sedang dalam antrean menunggu verifikasi.</p>
                    )}
                  </div>
                )}

                {/* 5. Tab Galeri */}
                {modalTab === 'galeri' && (
                  <PhotoProvider>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                      {filteredModalItems.map((item) => {
                        const imgUrl = resolveItemImageUrl(item);
                        return (
                          <div key={item.id} className="bg-white dark:bg-slate-800 rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 shadow-sm flex flex-col">
                            <div className="aspect-square bg-slate-100 dark:bg-slate-800 relative flex items-center justify-center overflow-hidden">
                              {imgUrl ? (
                                <PhotoView src={imgUrl}>
                                  <img
                                    src={imgUrl}
                                    alt={item.kode_item}
                                    className="w-full h-full object-cover cursor-pointer"
                                    onError={(e) => { e.target.style.display = 'none'; }}
                                  />
                                </PhotoView>
                              ) : (
                                <div className="text-center text-slate-400 p-2">
                                  <IconPhoto size={24} className="mx-auto opacity-40 mb-1" />
                                  <span className="text-[10px]">No Photo</span>
                                </div>
                              )}
                              <div className="absolute top-1.5 left-1.5 bg-slate-900/80 text-amber-400 text-[10px] font-mono font-bold px-1.5 py-0.5 rounded">
                                {item.kode_item}
                              </div>
                            </div>
                            <div className="p-2 text-xs space-y-1">
                              <p className="font-bold text-slate-800 dark:text-slate-200 text-[11px] truncate">
                                {item.jenis_kategori?.nama_jenis || '-'}
                              </p>
                              <div className="flex justify-between items-center text-[11px] pt-1 border-t border-slate-100 dark:border-slate-700">
                                <span className="font-bold text-amber-600">{item.karat} K</span>
                                <span className="font-semibold text-slate-700 dark:text-slate-300">{parseFloat(item.berat || 0).toFixed(3)}g</span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </PhotoProvider>
                )}
              </div>

              {/* Modal Footer with Sticky Action Buttons for Mobile */}
              <div className="p-4 sm:p-5 pt-3 border-t border-slate-100 dark:border-slate-800 flex flex-col-reverse sm:flex-row items-center justify-between gap-2 flex-shrink-0 bg-white dark:bg-slate-900">
                <button
                  type="button"
                  onClick={() => setIsOpenDetailModal(false)}
                  className="w-full sm:w-auto px-4 py-2.5 text-xs font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-all cursor-pointer"
                >
                  Tutup
                </button>

                {selectedPermohonan.status === 'pending' && (
                  <div className="grid grid-cols-2 gap-2 w-full sm:w-auto">
                    <button
                      type="button"
                      onClick={() => {
                        setIsOpenDetailModal(false);
                        handleOpenVerification(selectedPermohonan, 'disetujui');
                      }}
                      className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white font-bold text-xs rounded-xl shadow-sm flex items-center justify-center gap-1.5 cursor-pointer"
                    >
                      <IconCheck size={16} />
                      <span>Setujui</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setIsOpenDetailModal(false);
                        handleOpenVerification(selectedPermohonan, 'ditolak');
                      }}
                      className="px-5 py-2.5 bg-rose-600 hover:bg-rose-700 active:bg-rose-800 text-white font-bold text-xs rounded-xl shadow-sm flex items-center justify-center gap-1.5 cursor-pointer"
                    >
                      <IconX size={16} />
                      <span>Tolak</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ========================================================================= */}
        {/* MODAL VERIFIKASI (Konfirmasi Persetujuan / Penolakan)                      */}
        {/* ========================================================================= */}
        {isOpenVerificationModal && verifyingPermohonan && (
          <div className="fixed inset-0 z-[999] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-white dark:bg-slate-900 w-full max-w-md rounded-2xl p-5 sm:p-6 shadow-2xl space-y-4 border border-slate-200 dark:border-slate-800">
              <div className="flex items-center justify-between pb-3 border-b border-slate-100 dark:border-slate-800">
                <h3 className="font-bold text-sm text-slate-800 dark:text-slate-100 flex items-center gap-2">
                  {actionType === 'disetujui' ? (
                    <span className="p-1 bg-emerald-100 dark:bg-emerald-950 text-emerald-600 rounded-lg">
                      <IconCheck size={18} />
                    </span>
                  ) : (
                    <span className="p-1 bg-rose-100 dark:bg-rose-950 text-rose-600 rounded-lg">
                      <IconX size={18} />
                    </span>
                  )}
                  <span>{actionType === 'disetujui' ? 'Setujui Permohonan Pindah' : 'Tolak Permohonan Pindah'}</span>
                </h3>
                <button
                  type="button"
                  onClick={() => setIsOpenVerificationModal(false)}
                  className="text-slate-400 hover:text-slate-600 p-1 cursor-pointer"
                >
                  <IconX size={18} />
                </button>
              </div>

              {/* Rangkuman Singkat */}
              <div className="p-3.5 bg-slate-50 dark:bg-slate-800/60 rounded-xl text-xs space-y-1.5 border border-slate-200/80 dark:border-slate-700">
                <div className="flex justify-between">
                  <span className="text-slate-500">No. Request:</span>
                  <span className="font-mono font-bold text-amber-600 dark:text-amber-400">{verifyingPermohonan.no_permohonan}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Toko Asal:</span>
                  <span className="font-semibold text-slate-800 dark:text-slate-200">
                    {verifyingPermohonan.cabang_asal?.nama_cabang ? verifyingPermohonan.cabang_asal.nama_cabang.replace(/Etalase/gi, 'Brankas') : '-'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Total Mutasi:</span>
                  <span className="font-bold text-slate-800 dark:text-slate-200">
                    {verifyingPermohonan.total_pcs} PCS ({parseFloat(verifyingPermohonan.total_berat).toFixed(3)} gr)
                  </span>
                </div>
              </div>

              <form onSubmit={handleSubmitVerification} className="space-y-4">
                <div>
                  <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1.5">
                    Catatan Verifikasi {actionType === 'ditolak' && <span className="text-rose-500">* (Wajib diisi jika menolak)</span>}
                  </label>
                  <textarea
                    rows={3}
                    placeholder={actionType === 'disetujui' ? 'Catatan verifikasi opsional...' : 'Jelaskan alasan penolakan permohonan...'}
                    value={catatanVerifikasi}
                    onChange={(e) => setCatatanVerifikasi(e.target.value)}
                    required={actionType === 'ditolak'}
                    className="w-full p-3 text-xs border border-slate-300 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 placeholder:text-slate-400 outline-none focus:ring-2 focus:ring-amber-500 resize-none"
                  />
                </div>

                <div className="flex justify-end gap-2 pt-2 border-t border-slate-100 dark:border-slate-800">
                  <button
                    type="button"
                    onClick={() => setIsOpenVerificationModal(false)}
                    className="px-4 py-2.5 text-xs font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl cursor-pointer"
                  >
                    Batal
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`px-5 py-2.5 text-xs font-bold text-white rounded-xl shadow-md transition-all flex items-center gap-1.5 cursor-pointer ${
                      actionType === 'disetujui'
                        ? 'bg-emerald-600 hover:bg-emerald-700'
                        : 'bg-rose-600 hover:bg-rose-700'
                    }`}
                  >
                    {isSubmitting ? 'Memproses...' : actionType === 'disetujui' ? 'Lanjut ke PIN & Setujui' : 'Lanjut ke PIN & Tolak'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ========================================================================= */}
        {/* PAGINATED IMAGE MODAL                                                     */}
        {/* ========================================================================= */}
        {isOpenImageModal && selectedImagePermohonan && (
          <div className="fixed inset-0 z-[999] bg-black/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-5">
            <div className="bg-white dark:bg-slate-900 w-full max-w-5xl rounded-2xl p-5 shadow-2xl border border-slate-200 dark:border-slate-800 h-[88vh] flex flex-col">
              <div className="flex items-center justify-between pb-3 border-b border-slate-100 dark:border-slate-800 flex-shrink-0">
                <div>
                  <h3 className="font-bold text-sm sm:text-base text-slate-900 dark:text-slate-100">
                    Galeri Foto: <span className="font-mono text-amber-600">{selectedImagePermohonan.no_permohonan}</span>
                  </h3>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {selectedImagePermohonan.total_pcs} Item ({parseFloat(selectedImagePermohonan.total_berat).toFixed(3)} gr)
                    {totalModalPermohonan > 0 && (
                      <span className="ml-2 font-semibold text-slate-700 dark:text-slate-300">
                        • Total Modal: {new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR' }).format(totalModalPermohonan)}
                      </span>
                    )}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setIsOpenImageModal(false)}
                  className="text-slate-400 hover:text-slate-600 p-1 cursor-pointer"
                >
                  <IconX size={20} />
                </button>
              </div>

              {/* Scrollable Images */}
              <div className="flex-1 overflow-y-auto min-h-0 py-3 pr-1">
                {isImageLoading ? (
                  <div className="flex justify-center items-center h-full">
                    <span className="loading loading-spinner text-amber-500"></span>
                  </div>
                ) : imageItems.length === 0 ? (
                  <div className="flex justify-center items-center h-full text-slate-400 text-xs">
                    Tidak ada item foto ditemukan.
                  </div>
                ) : (
                  <PhotoProvider>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                      {imageItems.map((pi) => {
                        const item = pi.item;
                        if (!item) return null;
                        const imgUrl = resolveItemImageUrl(item);

                        return (
                          <div key={pi.id} className="bg-white dark:bg-slate-800 rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 flex flex-col shadow-sm">
                            <div className="aspect-square bg-slate-100 dark:bg-slate-800 relative flex items-center justify-center overflow-hidden">
                              {imgUrl ? (
                                <PhotoView src={imgUrl}>
                                  <img
                                    src={imgUrl}
                                    alt={item.kode_item}
                                    className="w-full h-full object-cover cursor-pointer"
                                    onError={(e) => { e.target.style.display = 'none'; }}
                                  />
                                </PhotoView>
                              ) : (
                                <div className="text-slate-400 text-center p-2">
                                  <IconPhoto size={24} className="mx-auto opacity-40 mb-1" />
                                  <span className="text-[10px]">No Image</span>
                                </div>
                              )}
                              <div className="absolute top-1.5 left-1.5 bg-slate-900/80 text-amber-400 text-[10px] font-mono font-bold px-1.5 py-0.5 rounded">
                                {item.kode_item}
                              </div>
                            </div>
                            <div className="p-2.5 text-xs space-y-1">
                              <p className="font-bold text-slate-800 dark:text-slate-100 text-[11px] truncate">
                                {item.jenis_kategori?.nama_jenis || '-'}
                              </p>
                              <div className="flex justify-between items-center text-[11px] pt-1 border-t border-slate-100 dark:border-slate-700">
                                <span className="font-bold text-amber-600">{item.karat} K</span>
                                <span className="font-semibold text-slate-700 dark:text-slate-300">{parseFloat(item.berat || 0).toFixed(3)}g</span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </PhotoProvider>
                )}
              </div>

              {/* Image Modal Pagination */}
              {imageLastPage > 1 && (
                <div className="flex justify-center items-center gap-2 pt-3 border-t border-slate-100 dark:border-slate-800 flex-shrink-0">
                  <button
                    disabled={imagePage === 1 || isImageLoading}
                    onClick={() => setImagePage((p) => p - 1)}
                    className="px-3 py-1.5 text-xs font-semibold rounded-lg border bg-white dark:bg-slate-800 disabled:opacity-40 cursor-pointer"
                  >
                    Prev
                  </button>
                  <span className="text-xs font-bold text-slate-600 dark:text-slate-300 px-3 py-1 bg-slate-100 dark:bg-slate-800 rounded-lg">
                    {imagePage} / {imageLastPage}
                  </span>
                  <button
                    disabled={imagePage === imageLastPage || isImageLoading}
                    onClick={() => setImagePage((p) => p + 1)}
                    className="px-3 py-1.5 text-xs font-semibold rounded-lg border bg-white dark:bg-slate-800 disabled:opacity-40 cursor-pointer"
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Security PIN Modal */}
      {showPinModalVerification && (
        <PinBrankas
          onSuccess={() => {
            executeVerification();
          }}
        />
      )}
    </Layouts>
  );
};

export default VerifikasiPermohonanBrankasV2;
